import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  LinearProgress,
  Button,
  TextField,
  Autocomplete
} from '@mui/material';
import { Edit, Delete, AddComment, Add, Check, Close } from '@mui/icons-material';
import { useQuery, useMutation } from '@apollo/client/react';
import { useNavigate } from 'react-router-dom';
import { TASKS_LIST_QUERY, TYPE_OF_TASKS_QUERY, PEOPLE_QUERY, CREATE_OR_UPDATE_TASK, CREATE_OR_UPDATE_TYPE_OF_TASK } from '../../graphql/tasks';
import { GET_COMPANIES, GET_ELEMENT } from '../../graphql/projects';
import { BOARDS_LIST_QUERY } from '../../graphql/boards';
import LoadingOverlay from '../common/LoadingOverlay';
import TaskFormModal from '../tasks/TaskFormModal';
import TaskViewModal from '../tasks/TaskViewModal';

const ElementTasksList = ({ projectId, unitId, unitName, onDelete, onComment }) => {
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [newTaskName, setNewTaskName] = useState('');
  const [editingCompanyTaskId, setEditingCompanyTaskId] = useState(null);
  const [editingResponsibleTaskId, setEditingResponsibleTaskId] = useState(null);
  const [editingTypesTaskId, setEditingTypesTaskId] = useState(null);
  const [editingCompanyIds, setEditingCompanyIds] = useState([]);
  const [editingResponsibleIds, setEditingResponsibleIds] = useState([]);
  const [editingTaskTypes, setEditingTaskTypes] = useState([]);
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [taskViewModalOpen, setTaskViewModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);

  const { data: typeOfTasksData, refetch: refetchTypes } = useQuery(TYPE_OF_TASKS_QUERY, {
    fetchPolicy: 'cache-first'
  });

  const { data: companiesData } = useQuery(GET_COMPANIES, {
    fetchPolicy: 'cache-first'
  });

  const { data: elementData } = useQuery(GET_ELEMENT, {
    variables: { id: unitId },
    skip: !unitId,
    fetchPolicy: 'cache-first'
  });

  const { data: projectData } = useQuery(GET_ELEMENT, {
    variables: { id: projectId },
    skip: !projectId,
    fetchPolicy: 'cache-first'
  });

  const elementCompanyIds = elementData?.element?.company_ids || [];
  const projectCompanyIds = projectData?.element?.company_ids || [];

  const activeCompanyIds = elementCompanyIds.length > 0 ? elementCompanyIds : projectCompanyIds;

  const { data: peopleData } = useQuery(PEOPLE_QUERY, {
    variables: { 
      page: 1, 
      size: 10000,
      filters: {
        active: "true",
        company_ids: activeCompanyIds
      }
    },
    skip: activeCompanyIds.length === 0,
    fetchPolicy: 'cache-first'
  });

  const elementPeople = peopleData?.people?.list || [];

  const elementCompanies = (companiesData?.companies?.list || []).filter(c => activeCompanyIds.includes(c.id));

  const { data: boardsData } = useQuery(BOARDS_LIST_QUERY, {
    fetchPolicy: 'cache-first'
  });

  const { data, loading, refetch } = useQuery(TASKS_LIST_QUERY, {
    variables: { 
      page: 1, 
      size: 1000,
      sort_column: 'id',
      sort_direction: 'ASC',
      filters: {
        project_id: projectId ? parseInt(projectId) : null,
        element_id: unitId ? parseInt(unitId) : null
      }
    },
    fetchPolicy: 'network-only',
    skip: !projectId || !unitId
  });

  const [createTask] = useMutation(CREATE_OR_UPDATE_TASK);
  const [updateTask] = useMutation(CREATE_OR_UPDATE_TASK);
  const [createOrUpdateTypeOfTask] = useMutation(CREATE_OR_UPDATE_TYPE_OF_TASK);

  const getPriorityLabel = (priority) => {
    const labels = ['Highest', 'High', 'Normal', 'Low', 'Lowest'];
    return labels[priority] || 'Normal';
  };

  const getPriorityColor = (priority) => {
    const colors = ['error', 'warning', 'info', 'success', 'default'];
    return colors[priority] || 'info';
  };

  const handleCreateTask = async () => {
    if (!newTaskName.trim()) return;
    try {
      await createTask({
        variables: {
          name: newTaskName,
          project_id: parseInt(projectId),
          element_id: parseInt(unitId),
          priority: 2,
          complete: 0
        }
      });
      setNewTaskName('');
      setCreating(false);
      await refetch();
    } catch (error) {
      // Handle error silently
    }
  };

  const handleCancelCreate = () => {
    setNewTaskName('');
    setCreating(false);
  };

  const handleEditCompany = (task) => {
    setEditingCompanyTaskId(task.id);
    setEditingCompanyIds(task.companies?.map(c => parseInt(c.id)) || []);
  };

  const handleEditResponsible = (task) => {
    setEditingResponsibleTaskId(task.id);
    setEditingResponsibleIds(task.responsible?.map(r => parseInt(r.id)) || []);
  };

  const handleEditTypes = (task) => {
    setEditingTypesTaskId(task.id);
    // Store both existing IDs and new names
    setEditingTaskTypes(task.task_type?.map(t => ({ id: parseInt(t.id), name: t.name })) || []);
  };

  const handleSaveCompany = async (taskId) => {
    try {
      await updateTask({
        variables: {
          id: taskId,
          company_ids: editingCompanyIds
        }
      });
      setEditingCompanyTaskId(null);
      setEditingCompanyIds([]);
      await refetch();
    } catch (error) {
      // Handle error silently
    }
  };

  const handleCompanyKeyDown = (event, taskId) => {
    if (event.key === 'Enter') {
      handleSaveCompany(taskId);
    }
  };

  const handleSaveResponsible = async (taskId) => {
    try {
      await updateTask({
        variables: {
          id: taskId,
          responsible_ids: editingResponsibleIds
        }
      });
      setEditingResponsibleTaskId(null);
      setEditingResponsibleIds([]);
      await refetch();
    } catch (error) {
      // Handle error silently
    }
  };

  const handleResponsibleKeyDown = (event, taskId) => {
    if (event.key === 'Enter') {
      handleSaveResponsible(taskId);
    }
  };

  const handleSaveTypes = async (taskId) => {
    try {
      // Separate existing IDs and new names
      const existingIds = [];
      const newNames = [];
      
      editingTaskTypes.forEach(item => {
        if (typeof item === 'object' && item.id) {
          existingIds.push(item.id);
        } else if (typeof item === 'string') {
          newNames.push(item);
        }
      });
      
      // Create new types and get their IDs
      const newTypeIds = [];
      for (const name of newNames) {
        const { data } = await createOrUpdateTypeOfTask({ variables: { name } });
        if (data?.createOrUpdateTypeOfTask?.type_of_task?.id) {
          newTypeIds.push(parseInt(data.createOrUpdateTypeOfTask.type_of_task.id));
        }
      }
      
      await updateTask({
        variables: {
          id: taskId,
          dictionary_task_type_ids: [...existingIds, ...newTypeIds]
        }
      });
      setEditingTypesTaskId(null);
      setEditingTaskTypes([]);
      await refetchTypes();
      await refetch();
    } catch (error) {
      console.error('Error saving types:', error);
    }
  };

  const handleCancelEditCompany = () => {
    setEditingCompanyTaskId(null);
    setEditingCompanyIds([]);
  };

  const handleCancelEditResponsible = () => {
    setEditingResponsibleTaskId(null);
    setEditingResponsibleIds([]);
  };

  const handleCancelEditTypes = () => {
    setEditingTypesTaskId(null);
    setEditingTaskTypes([]);
  };

  if (!projectId || !unitId) return null;
  if (loading) return <LoadingOverlay />;

  const tasks = data?.tasks?.list?.filter(task => task !== null) || [];

  return (
    <Paper sx={{ p: 3, mt: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Tasks for {unitName || 'Selected Element'}
        </Typography>
      </Box>
      {creating ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body2" sx={{ flexShrink: 0 }}>Task name:</Typography>
          <TextField
            value={newTaskName}
            onChange={(e) => setNewTaskName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleCreateTask();
              }
            }}
            variant="standard"
            size="small"
            fullWidth
            autoFocus
            placeholder="Enter task name"
          />
          <IconButton onClick={handleCreateTask} color="success" size="small">
            <Check />
          </IconButton>
          <IconButton onClick={handleCancelCreate} color="error" size="small">
            <Close />
          </IconButton>
        </Box>
      ) : (
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          onClick={() => setCreating(true)}
          sx={{ mb: 2 }}
        >
          Create Task
        </Button>
      )}
      {tasks.length === 0 ? (
        <Box sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            No tasks found for this element
          </Typography>
        </Box>
      ) : (
        <TableContainer>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Board/Column</TableCell>
              <TableCell>Company</TableCell>
              <TableCell>Responsible</TableCell>
              <TableCell>Types</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Complete</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tasks.map((task) => (
              <TableRow
                key={task.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => {
                  setSelectedTask(task);
                  setTaskViewModalOpen(true);
                }}
              >
                <TableCell>{task.id}</TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {task.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  {task.board_column_id ? (
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {boardsData?.boards?.find(b => b.board_columns?.some(c => c.id === task.board_column_id?.toString()))?.name || '-'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {boardsData?.boards?.flatMap(b => b.board_columns)?.find(c => c.id === task.board_column_id?.toString())?.name || '-'}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">-</Typography>
                  )}
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  {editingCompanyTaskId === task.id ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Autocomplete
                        multiple
                        options={elementCompanies}
                        getOptionLabel={(option) => option.name}
                        value={elementCompanies.filter(c => editingCompanyIds.includes(parseInt(c.id)))}
                        onChange={(e, newValue) => setEditingCompanyIds(newValue.map(c => parseInt(c.id)))}
                        size="small"
                        sx={{ minWidth: 200 }}
                        renderInput={(params) => (
                          <TextField 
                            {...params} 
                            variant="standard" 
                            onKeyDown={(e) => handleCompanyKeyDown(e, task.id)}
                          />
                        )}
                      />
                      <IconButton onClick={() => handleSaveCompany(task.id)} color="success" size="small">
                        <Check fontSize="small" />
                      </IconButton>
                      <IconButton onClick={handleCancelEditCompany} color="error" size="small">
                        <Close fontSize="small" />
                      </IconButton>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }} onClick={() => handleEditCompany(task)}>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {task.companies?.length > 0 ? (
                          task.companies.map((company, index) => (
                            <Chip key={index} label={company.name} size="small" variant="outlined" />
                          ))
                        ) : (
                          <Typography variant="body2">-</Typography>
                        )}
                      </Box>
                      <IconButton size="small">
                        <Edit fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  {editingResponsibleTaskId === task.id ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Autocomplete
                        multiple
                        options={elementPeople}
                        getOptionLabel={(option) => `${option.first_name} ${option.last_name}`}
                        value={elementPeople.filter(p => editingResponsibleIds.includes(parseInt(p.id)))}
                        onChange={(e, newValue) => setEditingResponsibleIds(newValue.map(p => parseInt(p.id)))}
                        size="small"
                        sx={{ minWidth: 200 }}
                        renderInput={(params) => (
                          <TextField 
                            {...params} 
                            variant="standard" 
                            onKeyDown={(e) => handleResponsibleKeyDown(e, task.id)}
                          />
                        )}
                      />
                      <IconButton onClick={() => handleSaveResponsible(task.id)} color="success" size="small">
                        <Check fontSize="small" />
                      </IconButton>
                      <IconButton onClick={handleCancelEditResponsible} color="error" size="small">
                        <Close fontSize="small" />
                      </IconButton>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }} onClick={() => handleEditResponsible(task)}>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {task.responsible?.length > 0 ? (
                          task.responsible.map((person, index) => (
                            <Chip 
                              key={index} 
                              label={
                                <Box sx={{ textAlign: 'center', lineHeight: 1.2 }}>
                                  <div>{person.last_name} {person.first_name}</div>
                                  <div style={{ fontSize: '0.7em' }}>({person.uuid})</div>
                                </Box>
                              }
                              size="small" 
                              variant="outlined"
                              sx={{ height: 'auto', py: 0.5 }}
                            />
                          ))
                        ) : (
                          <Typography variant="body2">-</Typography>
                        )}
                      </Box>
                      <IconButton size="small">
                        <Edit fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  {editingTypesTaskId === task.id ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Autocomplete
                        multiple
                        freeSolo
                        options={typeOfTasksData?.type_of_tasks || []}
                        getOptionLabel={(option) => typeof option === 'string' ? option : option.name}
                        value={editingTaskTypes}
                        onChange={(e, newValue) => setEditingTaskTypes(newValue)}
                        size="small"
                        sx={{ minWidth: 200 }}
                        renderInput={(params) => (
                          <TextField 
                            {...params} 
                            variant="standard" 
                            placeholder="Type or select..." 
                          />
                        )}
                      />
                      <IconButton onClick={() => handleSaveTypes(task.id)} color="success" size="small">
                        <Check fontSize="small" />
                      </IconButton>
                      <IconButton onClick={handleCancelEditTypes} color="error" size="small">
                        <Close fontSize="small" />
                      </IconButton>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }} onClick={() => handleEditTypes(task)}>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {task.task_type?.length > 0 ? (
                          task.task_type.map((type, index) => (
                            <Chip key={index} label={type.name} size="small" variant="outlined" />
                          ))
                        ) : (
                          <Typography variant="body2">-</Typography>
                        )}
                      </Box>
                      <IconButton size="small">
                        <Edit fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={getPriorityLabel(task.priority)}
                    color={getPriorityColor(task.priority)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem', minWidth: 35 }}>
                      {task.complete}%
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={task.complete}
                      sx={{
                        width: 60,
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: 'grey.200',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 4,
                          backgroundColor: task.complete === 100 ? 'success.main' : 'primary.main'
                        }
                      }}
                    />
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                    <IconButton
                      size="small"
                      color="success"
                      onClick={(e) => {
                        e.stopPropagation();
                        onComment?.(task);
                      }}
                    >
                      <AddComment fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedTask(task);
                        setTaskModalOpen(true);
                      }}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete?.(task, () => refetch());
                      }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      )}
      <TaskFormModal 
        open={taskModalOpen} 
        onClose={() => {
          setTaskModalOpen(false);
          setSelectedTask(null);
        }}
        taskId={selectedTask?.id || null}
        onSave={async () => await refetch()}
      />
      <TaskViewModal
        open={taskViewModalOpen}
        onClose={() => {
          setTaskViewModalOpen(false);
          setSelectedTask(null);
        }}
        taskId={selectedTask?.id}
      />
    </Paper>
  );
};

export default ElementTasksList;